import { useEffect, useRef, useState } from 'react';

import authFetch from '../../../utils/authFetch';
import {
  normalizeStudyAlias,
  STUDY_ALIAS_TIMEOUT_MS,
  WORD_TEMPLATE_CONTENT_STORAGE_KEY,
  WORD_TEMPLATE_NAME_STORAGE_KEY,
} from './model/wordExport';
import {
  arrayBufferToBase64,
  downloadWordDocument,
} from './wordDocument';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
const STUDY_ALIAS_WARNING =
  'Не удалось сформировать сокращение области исследования. ' +
  'Документ будет выгружен без него.';
const WORD_EXPORT_STAGE = {
  IDLE: 'idle',
  APPLY_PLACEHOLDERS: 'apply-placeholders',
  FORM_CONCLUSION: 'form-conclusion',
  EXPORT_DOCUMENT: 'export-document',
  EXPORT_IN_PROGRESS: 'export-in-progress',
};

const requestStudyAlias = async (studyType) => {
  const currentStudyType = String(studyType ?? '').trim();
  if (!currentStudyType) {
    return '';
  }

  const controller = new AbortController();
  let timeoutId;
  const timeoutPromise = new Promise((_, reject) => {
    timeoutId = window.setTimeout(() => {
      controller.abort();
      const timeoutError = new Error('Истекло время ожидания LLM');
      timeoutError.name = 'AbortError';
      reject(timeoutError);
    }, STUDY_ALIAS_TIMEOUT_MS);
  });

  try {
    const response = await Promise.race([
      authFetch(`${API_URL}/generate-study-alias/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          study_type: currentStudyType,
        }),
        signal: controller.signal,
      }),
      timeoutPromise,
    ]);

    if (!response.ok) {
      throw new Error(`Backend вернул статус ${response.status}`);
    }

    const result = await response.json();
    const studyAlias = normalizeStudyAlias(result?.data?.alias);
    if (!result?.success || !studyAlias) {
      throw new Error('Backend не вернул корректное сокращение исследования');
    }

    return studyAlias;
  } finally {
    window.clearTimeout(timeoutId);
  }
};

/**
 * Управляет пользовательским сценарием Word-экспорта:
 * последовательно применяет плейсхолдеры, формирует заключение и только затем
 * создаёт документ. Каждый подготовительный этап выполняется после отдельного
 * React-render, чтобы следующий этап всегда получал актуальные данные.
 */
export const useWordExport = (
  wordDocumentData,
  {
    applyAllPlaceholders,
    formConclusion,
  } = {}
) => {
  const [customTemplateBase64, setCustomTemplateBase64] = useState(
    () => localStorage.getItem(WORD_TEMPLATE_CONTENT_STORAGE_KEY) || null
  );
  const [customTemplateFileName, setCustomTemplateFileName] = useState(
    () => localStorage.getItem(WORD_TEMPLATE_NAME_STORAGE_KEY) || ''
  );
  const [exportStage, setExportStage] = useState(WORD_EXPORT_STAGE.IDLE);
  const exportInProgressRef = useRef(false);
  const handledStageRef = useRef(null);
  const exportContextRef = useRef(null);
  const fileInputRef = useRef();
  const isExporting = exportStage !== WORD_EXPORT_STAGE.IDLE;

  const startWordExport = () => {
    if (exportInProgressRef.current) {
      return;
    }

    if (!customTemplateBase64) {
      alert('Перед выгрузкой загрузите шаблон Word');
      return;
    }

    exportInProgressRef.current = true;
    handledStageRef.current = null;
    setExportStage(WORD_EXPORT_STAGE.APPLY_PLACEHOLDERS);
  };

  useEffect(() => {
    exportContextRef.current = {
      applyAllPlaceholders,
      formConclusion,
      templateBase64: customTemplateBase64,
      wordDocumentData,
    };
  });

  useEffect(() => {
    if (
      exportStage === WORD_EXPORT_STAGE.IDLE ||
      exportStage === WORD_EXPORT_STAGE.EXPORT_IN_PROGRESS ||
      handledStageRef.current === exportStage
    ) {
      return;
    }

    handledStageRef.current = exportStage;
    const context = exportContextRef.current;

    const failPreparation = (error) => {
      console.error('Ошибка подготовки данных для Word-экспорта:', error);
      alert('Не удалось подготовить данные для экспорта в Word');
      exportInProgressRef.current = false;
      handledStageRef.current = null;
      setExportStage(WORD_EXPORT_STAGE.IDLE);
    };

    try {
      if (exportStage === WORD_EXPORT_STAGE.APPLY_PLACEHOLDERS) {
        context?.applyAllPlaceholders?.();
        setExportStage(WORD_EXPORT_STAGE.FORM_CONCLUSION);
        return;
      }

      if (exportStage === WORD_EXPORT_STAGE.FORM_CONCLUSION) {
        context?.formConclusion?.();
        setExportStage(WORD_EXPORT_STAGE.EXPORT_DOCUMENT);
        return;
      }

      if (exportStage === WORD_EXPORT_STAGE.EXPORT_DOCUMENT) {
        setExportStage(WORD_EXPORT_STAGE.EXPORT_IN_PROGRESS);

        const exportDocument = async () => {
          try {
            let studyAlias = '';
            try {
              studyAlias = await requestStudyAlias(
                context.wordDocumentData.studyType
              );
            } catch (error) {
              console.error(
                'Ошибка формирования сокращения исследования:',
                error
              );
              alert(STUDY_ALIAS_WARNING);
            }

            await downloadWordDocument({
              templateBase64: context.templateBase64,
              ...context.wordDocumentData,
              studyAlias,
            });
          } catch (error) {
            console.error('Ошибка экспорта:', error);
            alert('Не удалось экспортировать в Word');
          } finally {
            exportInProgressRef.current = false;
            handledStageRef.current = null;
            setExportStage(WORD_EXPORT_STAGE.IDLE);
          }
        };

        exportDocument();
      }
    } catch (error) {
      failPreparation(error);
    }
  }, [exportStage]);

  const handleTemplateFileChange = async (event) => {
    const file = event.target.files && event.target.files[0];

    if (!file) {
      return;
    }

    if (!file.name.toLowerCase().endsWith('.docx')) {
      alert('Можно загрузить только файл Word в формате .docx');
      event.target.value = '';
      return;
    }

    try {
      const fileBuffer = await file.arrayBuffer();
      const templateBase64 = arrayBufferToBase64(fileBuffer);
      setCustomTemplateBase64(templateBase64);
      setCustomTemplateFileName(file.name);
      localStorage.setItem(
        WORD_TEMPLATE_CONTENT_STORAGE_KEY,
        templateBase64
      );
      localStorage.setItem(WORD_TEMPLATE_NAME_STORAGE_KEY, file.name);
    } catch (error) {
      console.error('Ошибка загрузки шаблона Word:', error);
      alert('Не удалось загрузить шаблон Word');
    } finally {
      event.target.value = '';
    }
  };

  return {
    customTemplateFileName,
    fileInputRef,
    handleTemplateFileChange,
    hasCustomTemplate: Boolean(customTemplateBase64),
    isExporting,
    startWordExport,
  };
};
